import React from 'react';
import styled from 'styled-components';
import Navbar from './Navbar';
import DesktopMenu from './DesktopMenu';
import Footer from './Footer';
import coverImg from '../images/creative_signal_ritual_cover.png';

const GUMROAD_URL = 'https://spectralcenter.gumroad.com/l/signal';

const CreativeSignalRitual = () => {
    return (
        <>
            <Navbar/>
            <DeskWrap>
                <DesktopMenu/>
                <Wrapper>
                    <Body>
                        <h1>Creative Signal Ritual</h1>
                        <Sub>21 Days to Restore Creative Reception</Sub>
                        <Price>€97</Price>
                        <CoverImage src={coverImg} alt="Creative Signal Ritual" />

                        <p>You don't have a creativity problem. You have a signal problem.</p>

                        <p>When the nervous system is overloaded, overstimulated, or under pressure, it reduces sensitivity. Signal is still present. But reception weakens.</p>
                        <p>The Creative Signal Ritual is a 21-day guided process designed to restore signal clarity and creative flow — not by forcing output, but by stabilizing the receiver.</p>

                        <p>You might recognize this: you can still produce work, but it feels heavier. Ideas don't arrive as easily as they used to. You overthink instead of receive. Rest makes you anxious instead of inspired. You miss that feeling of something moving through you.</p>

                        <p>You haven't lost your creativity. Your nervous system has adapted to pressure. And adaptation can be reversed.</p>

                        <p>Creativity is not something you manufacture. It is something you detect. Your nervous system functions like a receiver: when it feels safe and stable, signal becomes clear; when it feels threatened or overstimulated, interference increases. Most people respond by trying harder. But effort without regulation increases noise.</p>

                        <p>This ritual works differently. It reduces interference. It increases sensitivity. It restores reception.</p>

                        <h3>What it is</h3>
                        <p>A structured 21-day nervous system recalibration for artists, writers, and creative practitioners. Each day, you receive one 10–15 minute guided audio session and one short writing prompt to strengthen signal detection, plus lifetime access to all the recordings. The structure is simple. The effects accumulate.</p>

                        <h3>How it works — the three phases</h3>

                        <h4>Week 1 — Devotion: Stabilizing the Receiver</h4>
                        <p>You build continuity. You show up independent of inspiration. The nervous system learns predictability. Predictability creates safety. Safety increases sensitivity.</p>

                        <h4>Week 2 — Descent: Reducing Interference</h4>
                        <p>You work with uncertainty, limitation, and negative capability. You increase tolerance for ambiguity. You reduce the urge to force resolution. Noise decreases. Signal clarifies.</p>

                        <h4>Week 3 — Transmission: Trust & Flow</h4>
                        <p>You stabilize reception. You shift from effort to flow. Ideas begin arriving more naturally. Work becomes lighter — not because you try harder, but because you're tuned.</p>

                        <h3>This is for you if</h3>
                        <p>You're a serious creative practitioner. You know what it feels like when creativity is alive, and you can sense that something has interfered with your signal. You want restoration, not hacks. You're willing to commit fifteen minutes a day.</p>

                        <p>This is not a productivity challenge. It is not motivational content. It is nervous system training.</p>

                        <h3>What tends to shift</h3>
                        <p>Participants typically report less resistance when beginning work, ideas arriving outside of work time, more calm while creating, less self-monitoring, more continuity, and a return of intuitive clarity. Nothing dramatic. Just stable restoration.</p>

                        <BuyButton href={GUMROAD_URL} target="_blank" rel="noopener noreferrer">Start the Ritual</BuyButton>
                    </Body>
                </Wrapper>
            </DeskWrap>
            <Footer/>
        </>
    )
}

const DeskWrap = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;

    @media (min-width: 1000px) {
        flex-direction: row;
    }
`

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 90vh;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;

    @media (min-width: 1000px) {
        padding: 2rem;
    }
`

const Body = styled.div`
    font-family: var(--font-body);
    width: 95%;
    padding: 0 1rem;
    max-width: 100%;

    h1 {
        font-size: 1.8rem;
        margin-bottom: 0.4rem;
        color: #333;
    }

    h3 {
        font-size: 1.2rem;
        margin: 1.75rem 0 0.75rem;
        color: #333;
    }

    h4 {
        font-size: 1.05rem;
        margin: 1.25rem 0 0.5rem;
        color: #333;
    }

    p {
        line-height: 1.7;
        margin-bottom: 1.1rem;
        color: #444;
    }

    @media (min-width: 1000px) {
        max-width: 65ch;
        padding: 0 1rem;
    }
`

const Sub = styled.p`
    font-style: italic;
    font-size: 1.05rem;
    color: #666;
    margin: 0 0 0.75rem;
`

const Price = styled.p`
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin: 0 0 1.25rem;
`

const CoverImage = styled.img`
    width: 100%;
    max-width: 480px;
    display: block;
    margin: 0 0 1.75rem;
    transform: rotate(1.2deg);
    box-shadow: 0 4px 14px rgba(0,0,0,0.15);
`

const BuyButton = styled.a`
    display: inline-block;
    margin-top: 1rem;
    padding: 0.85rem 1.75rem;
    background: #333;
    color: #fff;
    text-decoration: none;
    border-radius: 0.4rem;
    font-weight: 600;
    transition: background 0.2s ease;

    &:hover {
        background: #000;
    }
`

export default CreativeSignalRitual;
